/**
 * ============================================================================
 * Ring2All Platform & Frontend Subsystem
 * 
 * @file UserAppsSection.tsx
 * @author Ring2All Team
 * @copyright Ring2All LLC
 * 
 * @description
 * UI Component and module logic for UserAppsSection.tsx.
 * ============================================================================
 */

﻿/*â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
 ðŸŒ Project: Ring2All Platform
 ðŸ§‘â€ðŸ’» Author: Ring2All Team
 ðŸ¢ Company: Ring2All LLC
 ðŸ“„ File: components\UserAppsSection.tsx
 ðŸ—“ï¸ Created: 2026-05-02
 ðŸ§© Description:
    Implementation of the Users module.
â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€*/

import { useEffect, useState, useMemo } from 'react';
import { Toggle } from '../../../components/ui/Toggle';
import { FormBox } from '../../../components/ui/FormBox';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import { AppWindow, Monitor, ExternalLink, User, Settings, CreditCard } from 'lucide-react';
import { buildApiUrl, getApiHeaders } from '../../../config/api';
import { UserFormData } from '../hooks/useUserForm';
import { SearchableSelect } from '../../../components/ui/SearchableSelect';
import RoleProfileSelect from '../../../components/ui/RoleProfileSelect';
import LogProfileSelect from '../../../components/ui/LogProfileSelect';

export interface UserAppsSectionProps {
  formData: UserFormData & { profileId?: string, logProfileId?: string };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  profiles: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  logProfiles: any[];
  onInputChange: (field: keyof UserFormData | string, value: unknown) => void;
  t: (key: string, _default?: string) => string;
}

export function UserAppsSection({ formData, profiles, logProfiles, onInputChange, t }: UserAppsSectionProps) {
  const handleAppConfigChange = (
    appField: string,
    value: string | boolean
  ) => {
    onInputChange('appsConfigs', {
      ...formData.appsConfigs,
      [appField]: value
    });
  };

  const [switchboardRoles, setSwitchboardRoles] = useState<{id: number, name: string, description: string}[]>([]);
  const [isLoadingSwitchboardRoles, setIsLoadingSwitchboardRoles] = useState(false);

  const [switchboardLogProfiles, setSwitchboardLogProfiles] = useState<{id: number, name: string, description: string}[]>([]);
  const [isLoadingSwitchboardLogProfiles, setIsLoadingSwitchboardLogProfiles] = useState(false);


  useEffect(() => {
    let isMounted = true;
    const fetchSwitchboardData = async () => {
      setIsLoadingSwitchboardRoles(true);
      try {
        const response = await fetch(buildApiUrl('/api/apps/switchboard/roles'), {
          headers: getApiHeaders()
        });
        if (response.ok) {
          const result = await response.json();
          if (isMounted) setSwitchboardRoles(result.data || []);
        }
      } catch (error) {
        console.error('Failed to fetch switchboard roles:', error);
      } finally {
        if (isMounted) setIsLoadingSwitchboardRoles(false);
      }

      setIsLoadingSwitchboardLogProfiles(true);
      try {
        const response = await fetch(buildApiUrl('/api/apps/switchboard/log-profiles'), {
          headers: getApiHeaders()
        });
        if (response.ok) {
          const result = await response.json();
          if (isMounted) setSwitchboardLogProfiles(result.data || []);
        }
      } catch (error) {
        console.error('Failed to fetch switchboard log profiles:', error);
      } finally {
        if (isMounted) setIsLoadingSwitchboardLogProfiles(false);
      }
    };

    fetchSwitchboardData();
    return () => { isMounted = false; };
  }, []);

  // Ensure formData has the global profile properties
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const profileId = (formData as any).profileId || '';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const logProfileId = (formData as any).logProfileId || '';

  const formattedSwitchboardRoles = useMemo(() => {
    return switchboardRoles.map(r => ({ id: r.name, name: r.name, description: r.description }));
  }, [switchboardRoles]);

  const formattedSwitchboardLogProfiles = useMemo(() => {
    return switchboardLogProfiles.map(p => ({ id: p.id.toString(), name: p.name, description: p.description }));
  }, [switchboardLogProfiles]);


  const [aiAgents, setAiAgents] = useState<{id: number, name: string, description?: string}[]>([]);
  useEffect(() => {
    let isMounted = true;
    const fetchAiAgents = async () => {
      try {
        const response = await fetch(buildApiUrl('/api/telephony/ai-agents?limit=100&agentType=chat'), {
          headers: getApiHeaders()
        });
        if (response.ok) {
          const result = await response.json();
          if (isMounted) setAiAgents(result.data || []);
        }
      } catch (error) {
        console.error('Failed to fetch AI agents:', error);
      }
    };
    fetchAiAgents();
    return () => { isMounted = false; };
  }, []);

  useEffect(() => {
    if (formData.chatbotEnabled && !formData.chatbotAgentId && aiAgents.length > 0) {
      const defaultAgent = aiAgents.find((a: any) => a.isDefaultChatbot);
      if (defaultAgent) {
        onInputChange('chatbotAgentId', defaultAgent.id);
      }
    }
  }, [formData.chatbotEnabled, formData.chatbotAgentId, aiAgents, onInputChange]);

  return (
    <div className="space-y-4">
    <FormBox
      title={t('users.sections.apps', 'Available Apps')}
      icon={<AppWindow className="h-5 w-5" />}
    >
      <div className="w-full border border-border rounded-md pb-8">
        <table className="w-full text-sm text-left">
          <thead className="text-xs text-muted-foreground uppercase bg-muted/50 border-b border-border">
            <tr>
              <th className="px-4 py-3 font-medium">{t('users.apps.appName', 'App Name')}</th>
              <th className="px-4 py-3 font-medium text-center">{t('users.apps.status', 'Status')}</th>
              <th className="px-4 py-3 font-medium">{t('users.apps.role', 'Role / Role Profile')}</th>
              <th className="px-4 py-3 font-medium">{t('users.apps.logProfile', 'Log Profile')}</th>
              <th className="px-4 py-3 font-medium">{t('users.apps.url', 'Custom URL')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {/* Ring2All (Admin) Application */}
            <tr className="hover:bg-muted/10 transition-colors">
              <td className="px-4 py-3 align-middle font-medium text-foreground whitespace-nowrap">
                <div className="flex items-center gap-2">
                  <Settings className="h-4 w-4 text-primary" />
                  {t('users.apps.ring2all', 'Ring2All (Admin)')}
                </div>
              </td>
              <td className="px-4 py-3 align-middle text-center">
                <SimpleTooltip content={t('users.tooltips.enableAdmin', 'Enable or disable Core Admin access.')}>
                   <div className="flex justify-center">
                     <Toggle
                       value={formData.appsConfigs.coreEnabled ?? true}
                       onChange={(checked) => handleAppConfigChange('coreEnabled', checked)}
                       // eslint-disable-next-line @typescript-eslint/no-explicit-any
                       disabled={(formData as any).isPrimaryAdmin}
                     />
                   </div>
                </SimpleTooltip>
              </td>
              <td className="px-4 py-3 align-middle">
                <SimpleTooltip content={t('users.tooltips.profile', 'Choose Role Profile for System access.')}>
                    <div className="w-full min-w-[180px]">
                        <RoleProfileSelect
                            value={profileId}
                            onChange={(value) => onInputChange('profileId', value)}
                            options={profiles}
                            placeholder={t('users.selectRoleProfileReq', "Select role profile *")}
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            disabled={!(formData.appsConfigs.coreEnabled ?? true) || (formData as any).isPrimaryAdmin}
                            className={(formData.appsConfigs.coreEnabled ?? true) && !profileId && !(formData as any).isPrimaryAdmin ? '!border-red-500/50 !bg-red-500/5 shadow-[0_0_0_1px_rgba(239,68,68,0.3)]' : ''}
                        />
                    </div>
                </SimpleTooltip>
              </td>
              <td className="px-4 py-3 align-middle">
                <SimpleTooltip content={t('users.tooltips.logProfile', 'Choose Log Profile.')}>
                    <div className="w-full min-w-[180px]">
                        <LogProfileSelect
                            value={logProfileId}
                            onChange={(value) => onInputChange('logProfileId', value)}
                            options={logProfiles}
                            placeholder={t('users.selectLogProfileReq', "Select log profile *")}
                            disabled={!(formData.appsConfigs.coreEnabled ?? true)}
                            className={(formData.appsConfigs.coreEnabled ?? true) && !logProfileId ? '!border-red-500/50 !bg-red-500/5 shadow-[0_0_0_1px_rgba(239,68,68,0.3)]' : ''}
                        />
                    </div>
                </SimpleTooltip>
              </td>
              <td className="px-4 py-3 align-middle w-full">
                 <span className="text-muted-foreground italic px-3 text-xs">
                    {t('users.apps.notApplicable', 'N/A')}
                 </span>
              </td>
            </tr>

            {/* Switchboard Application */}
            <tr className="hover:bg-muted/10 transition-colors">
              <td className="px-4 py-3 align-middle font-medium text-foreground">
                <div className="flex items-center gap-2">
                  <Monitor className="h-4 w-4 text-primary" />
                  Switchboard
                </div>
              </td>
              <td className="px-4 py-3 align-middle text-center">
                <SimpleTooltip content={t('users.tooltips.enableSwitchboard', 'Enable or disable Switchboard application login.')}>
                   <div className="flex justify-center">
                     <Toggle
                       value={formData.appsConfigs.switchboardEnabled || false}
                       onChange={(checked) => handleAppConfigChange('switchboardEnabled', checked)}
                     />
                   </div>
                </SimpleTooltip>
              </td>
              <td className="px-4 py-3 align-middle">
                <SimpleTooltip content={t('users.tooltips.switchboardRole', 'Choose role inside Switchboard.')}>
                    <div className="w-full min-w-[180px]">
                        <RoleProfileSelect
                            value={formData.appsConfigs.switchboardRole || ''}
                            onChange={(value) => handleAppConfigChange('switchboardRole', value)}
                            options={formattedSwitchboardRoles}
                            placeholder={t('users.selectRoleReq', "Select role *")}
                            disabled={!formData.appsConfigs.switchboardEnabled || isLoadingSwitchboardRoles}
                            loading={isLoadingSwitchboardRoles}
                            className={formData.appsConfigs.switchboardEnabled && !formData.appsConfigs.switchboardRole ? '!border-red-500/50 !bg-red-500/5 shadow-[0_0_0_1px_rgba(239,68,68,0.3)]' : ''}
                        />
                    </div>
                </SimpleTooltip>
              </td>
              <td className="px-4 py-3 align-middle">
                <SimpleTooltip content={t('users.tooltips.switchboardLogProfile', 'Choose Log Profile for Switchboard events.')}>
                    <div className="w-full min-w-[180px]">
                        <LogProfileSelect
                            value={formData.appsConfigs.switchboardLogProfileId ? String(formData.appsConfigs.switchboardLogProfileId) : ''}
                            onChange={(value) => handleAppConfigChange('switchboardLogProfileId', value)}
                            options={formattedSwitchboardLogProfiles as unknown as any[]}
                            placeholder={t('users.selectLogProfileReq', "Select log profile *")}
                            disabled={!formData.appsConfigs.switchboardEnabled || isLoadingSwitchboardLogProfiles}
                            className={formData.appsConfigs.switchboardEnabled && !formData.appsConfigs.switchboardLogProfileId ? '!border-red-500/50 !bg-red-500/5 shadow-[0_0_0_1px_rgba(239,68,68,0.3)]' : ''}
                        />
                    </div>
                </SimpleTooltip>
              </td>
              <td className="px-4 py-3 align-middle w-full">
                 <div className="relative flex items-center w-full max-w-sm">
                    <ExternalLink className="absolute left-2.5 h-4 w-4 text-muted-foreground pointer-events-none" />
                    <input
                      type="text"
                      className="h-9 w-full rounded-md border border-input bg-background pl-9 pr-3 text-sm outline-none transition-colors disabled:opacity-50 placeholder:text-muted-foreground"
                      placeholder={t('users.apps.defaultUrl', 'Default URL')}
                      value={formData.appsConfigs.switchboardUrl as string || ''}
                      onChange={(e) => handleAppConfigChange('switchboardUrl', e.target.value)}
                      disabled={!formData.appsConfigs.switchboardEnabled}
                    />
                 </div>
              </td>
            </tr>

            {/* Portal Application */}
            <tr className="hover:bg-muted/10 transition-colors">
              <td className="px-4 py-3 align-middle font-medium text-foreground">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-primary" />
                  Portal
                </div>
              </td>
              <td className="px-4 py-3 align-middle text-center">
                <SimpleTooltip content={t('users.tooltips.enablePortal', 'Enable or disable User Portal login.')}>
                   <div className="flex justify-center">
                     <Toggle
                       value={formData.appsConfigs.portalEnabled || false}
                       onChange={(checked) => handleAppConfigChange('portalEnabled', checked)}
                     />
                   </div>
                </SimpleTooltip>
              </td>
              <td className="px-4 py-3 align-middle">
                 <span className="text-muted-foreground italic px-3 text-xs">
                    {t('users.apps.notApplicable', 'N/A')}
                 </span>
              </td>
              <td className="px-4 py-3 align-middle">
                 <span className="text-muted-foreground italic px-3 text-xs">
                    {t('users.apps.notApplicable', 'N/A')}
                 </span>
              </td>
              <td className="px-4 py-3 align-middle w-full">
                 <div className="relative flex items-center w-full max-w-sm">
                    <ExternalLink className="absolute left-2.5 h-4 w-4 text-muted-foreground pointer-events-none" />
                    <input
                      type="text"
                      className="h-9 w-full rounded-md border border-input bg-background pl-9 pr-3 text-sm outline-none transition-colors disabled:opacity-50 placeholder:text-muted-foreground"
                      placeholder={t('users.apps.defaultUrl', 'Default URL')}
                      value={formData.appsConfigs.portalUrl as string || ''}
                      onChange={(e) => handleAppConfigChange('portalUrl', e.target.value)}
                      disabled={!formData.appsConfigs.portalEnabled}
                    />
                 </div>
              </td>
            </tr>


          </tbody>
        </table>
      </div>
    </FormBox>

    {/* AI Assistant */}
    <FormBox
      title={t('extensions.advanced.chatbot.title', 'AI Assistant (Chatbot)')}
      icon={<Settings className="h-5 w-5" />}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-start">
        {/* Enable Chatbot */}
        <SimpleTooltip
          content={t('extensions.advanced.chatbot.enabled.tooltip', 'Enable AI chatbot for this user in the Portal')}
          title={t('extensions.advanced.chatbot.enabled.label', 'Enable Chatbot')}
          position="top"
          maxWidth="300px"
        >
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label className="text-left font-medium text-sm text-foreground">
            {t('extensions.advanced.chatbot.enabled.label', 'Enable Chatbot')}
          </label>
        </SimpleTooltip>
        <Toggle
          value={formData.chatbotEnabled ?? false}
          onChange={(value) => onInputChange('chatbotEnabled', value)}
        />

        {/* Chatbot Agent Selection */}
        {formData.chatbotEnabled && (
          <>
            <SimpleTooltip
              content={t('extensions.advanced.chatbot.agent.tooltip', 'Select the specific AI Agent personality to use')}
              title={t('extensions.advanced.chatbot.agent.label', 'Agent Personality')}
              position="top"
              maxWidth="300px"
            >
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label className="text-left font-medium text-sm text-foreground">
                {t('extensions.advanced.chatbot.agent.label', 'Agent Personality')}
              </label>
            </SimpleTooltip>
            <SearchableSelect
              id="chatbotAgentId"
              value={formData.chatbotAgentId ? String(formData.chatbotAgentId) : null}
              onChange={(val) => onInputChange('chatbotAgentId', val ? parseInt(val, 10) : null)}
              options={aiAgents.map((agent: any) => ({ value: String(agent.id), label: agent.agentName || agent.name || '' }))}
              placeholder={t('common.select', 'Select...')}
              searchPlaceholder={t('common.search', 'Search...')}
            />
          </>
        )}
      </div>
    </FormBox>
    </div>
  );
}


